import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/lib/toast";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface LoveState {
  counts: Record<string, number>;
  mine: Record<string, boolean>;
}

export function useLoves(scanIds: string[]) {
  const { user } = useAuth();
  const [state, setState] = useState<LoveState>({ counts: {}, mine: {} });

  const realIds = scanIds.filter((id) => UUID_RE.test(id));
  const stableKey = realIds.slice().sort().join(",");

  useEffect(() => {
    if (realIds.length === 0) return;
    let cancelled = false;
    supabase
      .from("loves")
      .select("scan_id, user_id")
      .in("scan_id", realIds)
      .then(({ data }) => {
        if (cancelled) return;
        const counts: Record<string, number> = {};
        const mine: Record<string, boolean> = {};
        for (const row of data ?? []) {
          counts[row.scan_id] = (counts[row.scan_id] ?? 0) + 1;
          if (user && row.user_id === user.id) mine[row.scan_id] = true;
        }
        setState({ counts, mine });
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey, user?.id]);

  const toggle = useCallback(async (scanId: string, ownerId: string | null) => {
    if (!user) { toast.error("Sign in to love posts"); return; }
    if (!UUID_RE.test(scanId)) { toast.info("This is a demo post — loves aren't saved"); return; }
    if (ownerId && ownerId === user.id) { toast.error("You can't love your own post"); return; }

    const isLoved = state.mine[scanId] === true;

    setState((prev) => ({
      counts: { ...prev.counts, [scanId]: Math.max(0, (prev.counts[scanId] ?? 0) + (isLoved ? -1 : 1)) },
      mine: { ...prev.mine, [scanId]: !isLoved },
    }));

    if (isLoved) {
      const { error } = await supabase.from("loves").delete()
        .eq("user_id", user.id).eq("scan_id", scanId);
      if (error) {
        setState((prev) => ({
          counts: { ...prev.counts, [scanId]: (prev.counts[scanId] ?? 0) + 1 },
          mine: { ...prev.mine, [scanId]: true },
        }));
        toast.error("Couldn't remove love");
      }
    } else {
      const { error } = await supabase.from("loves").upsert(
        { user_id: user.id, scan_id: scanId },
        { onConflict: "user_id,scan_id", ignoreDuplicates: true },
      );
      if (error) {
        setState((prev) => ({
          counts: { ...prev.counts, [scanId]: Math.max(0, (prev.counts[scanId] ?? 0) - 1) },
          mine: { ...prev.mine, [scanId]: false },
        }));
        toast.error("Couldn't save love");
      }
    }
  }, [user, state.mine]);

  return {
    countFor: (scanId: string) => state.counts[scanId] ?? 0,
    mineFor: (scanId: string) => state.mine[scanId] === true,
    toggle,
  };
}
